import { CurrentDeviceContext } from "@stadium/ui/src/contexts/responsive-content";
import isEmpty from "lodash/isEmpty";
import { useContext, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import dynamic from "next/dynamic";
import classNames from "classnames";

import { InternalButton } from "@sharedComponents/buttons";

import { useFlags } from "@stadium/services/launch-darkly";

import SwagCartNote from "@components/shared/cart/swag-cart-note";
import CartCheckoutCta from "@components/store/cart/cart-checkout-cta";
import ExitDemoModeCta from "@components/store/cart/exit-demo-mode-cta";
import CheckoutCurrencySelector from "@stadium/checkout/src/components/currency-selector";
import OrderSummary from "@stadium/checkout/src/components/order-summary";
import PromoCode from "@stadium/checkout/src/components/promo-code";
import CurrencySelector from "@stadium/ui/src/components/currency-selector";
import { CurrentUserContext } from "@contexts/current-user-provider";

import { convertPrice, formatPrice, getFormattedOrderCheckoutSummary } from "@helpers/currency";

import useRefreshCumulativeCart from "@hooks/cart/use-refresh-cumulative-cart";
import useOnCheckout from "@hooks/checkout/use-on-checkout";
import { useWithAnalytics } from "@stadium/services/analytics/hooks/use-with-analytics";
import { useStoreContext } from "@components/store/provider";
import { usePreviewModeContext } from "@contexts/preview-mode-context";
import useStoreCurrencyConverter from "@hooks/currency/use-store-currency-converter";
import { useCheckoutContext as useGlobalCheckoutContext } from "@stadium/checkout/src/contexts/checkout";
import Loaders from "@stadium/ui/src/components/loaders/loaders";

import { useSendItemsContext } from "@contexts/send-items-context";
import useUnavailableItemsModal from "@hooks/checkout/use-unavailable-items-modal";
import { useCheckoutContext } from "contexts/checkout-context";
import { useWorkspaceContext } from "@contexts/workspace-context";

import { refreshTotals } from "@actions/order";
import { useConfig } from "@stadium/hooks/lib/use-config";
import {
  getConversionRates,
  getCurrencies,
  getPlatformFeeInfoMessage,
  getPlatformFeeLabel,
  getPompLabel,
  getTaxRecoveryFeeLabel,
  getTaxRecoveryFeeRecipientInfoMessage,
  getUsCountryId,
} from "@stadium/redux-utils/selectors/configSelector";

import { getCountry } from "@selectors/country";
import {
  getCardOrderShopPointsContribution,
  getCardOrderShopPointsContributionWithoutTaxes,
  getCartCountryId,
  getCartCurrencyCode,
  getCartItemsCount,
  getCartOrderCurrency,
  getCartOrderFetched,
  getCartOrderGiftDetailId,
  getCartOrderId,
  getCartOrderNumber,
  getCartOrderOutstandingBalance,
  getCartOrderOutstandingBalanceWithoutTaxes,
  getCartOrderStadiumPointsContribution,
  getCartOrderStadiumPointsContributionWithoutTaxes,
  getCartOrderTeamPointsContribution,
  getCartOrderTeamPointsContributionWithoutTaxes,
  getCartOrderwalletMoneyContribution,
  getCartOrderWalletMoneyContributionWithoutTaxes,
  getCartPromoCode,
} from "@store/selectors/cart";
import {
  getIsLockerShop,
  getIsPricelessShop,
  getStoreCurrencyCode,
  getStoreCurrencyName,
} from "@store/selectors/store";

import { postHubspotFormData } from "@stadium/shared/src/apis/book-a-call";

import { DEFAULT_STC_CURRENCY } from "@stadium/constants/currency";
import { isCustomCurrency } from "@stadium/helpers/src/currency";

import { NEXT, SEND_TO_MYSELF, YOUR_BAG_OTHER, YOUR_BAG_SELF } from "@constants/june-so";
import {
  ASSET_MISSING_NOTE,
  MIN_CARD_CHARGE_IN_USD,
  PURCHASING_SWAG_NOTE,
} from "@constants/orders/cart";
import { PREVIEW_MODE_DEFAULT_ORDER_ID } from "@constants/preview-mode";
import { POINTS_CURRENCY_CODE } from "@constants/store";
import { USE_CORPORATE_BILLING } from "@stadium/checkout/src/constants/corporate-billing";
import { MY_WALLET } from "@stadium/constants/wallets";
import { HUBSPOT_LOCKER_SHOP_GET_A_CALL_FORM_GUID } from "@constants/env-variables";

import { appGenericAnnouncementBarFlag } from "@stadium/services/launch-darkly/constants";

import { isAppGenericAnnouncementBarFlagEnabled } from "@stadium/ui/src/helpers/app-generic-banner";

import { SHOPS } from "@stadium/constants";
import { PAY_TARGET } from "@constants/analytics/checkout";

import styles from "./main.module.scss";

const GetAQuoteModal = dynamic(() => import("@components/modals/get-a-quote"), {
  loading: () => (
    <div className={styles.loaderBackdrop}>
      <Loaders.Spinner />
    </div>
  ),
});

const { corporateBilled } = USE_CORPORATE_BILLING;

const CartCheckout = ({ editable, children, cartCheckoutClass = " " }) => {
  const dispatch = useDispatch();

  const { [appGenericAnnouncementBarFlag]: appGenericAnnouncementBarFlagValue } = useFlags();

  const { currentUserData } = useContext(CurrentUserContext);
  const { email: userEmail } = currentUserData;

  const [showGetAQuoteModal, setShowGetAQuoteModal] = useState(false);
  const { isSendToMyself } = useSendItemsContext();
  const { isActivePackageUser } = useWorkspaceContext();

  const {
    isGlobalCountry,
    showCurrencySelector,
    displayCurrency,
    setDisplayCurrency,
    storeDisplayCurrencyCode,
    isNewSwagmagicHost,
    storeNumber,
  } = useStoreContext();
  const currencies = useConfig(getCurrencies);

  const { refreshCart, postCumulativeCartRefresh } = useRefreshCumulativeCart();

  const appGenericAnnouncementBarFlagEnabled = isAppGenericAnnouncementBarFlagEnabled(
    appGenericAnnouncementBarFlagValue,
    SHOPS,
    isActivePackageUser,
  );

  const isLockerShop = useSelector(getIsLockerShop);
  const storeCurrencyName = useSelector(getStoreCurrencyName);
  const orderNumber = useSelector(getCartOrderNumber);
  const orderCountryId = useSelector(getCartCountryId);
  const orderCurrencyId = useSelector(getCartCurrencyCode);
  const selectedCountry = useSelector(getCountry);
  const platformFeeLabel = useConfig(getPlatformFeeLabel);
  const platformFeeTooltipText = useConfig(getPlatformFeeInfoMessage);
  const taxRecoveryFeeTooltipText = useConfig(getTaxRecoveryFeeRecipientInfoMessage);
  const taxRecoveryFeeLabel = useConfig(getTaxRecoveryFeeLabel);
  const conversionRates = useConfig(getConversionRates);
  const pompLabel = useConfig(getPompLabel);
  const orderId = useSelector(getCartOrderId);
  const itemsCount = useSelector(getCartItemsCount);
  const usCountryId = useConfig(getUsCountryId);
  const isPricelessShop = useSelector(getIsPricelessShop);
  const storeCurrencyCode = useSelector(getStoreCurrencyCode);
  const promoCode = useSelector(getCartPromoCode);
  const giftDetailId = useSelector(getCartOrderGiftDetailId);
  const outstandingBalance = useSelector(getCartOrderOutstandingBalance);
  const outstandingBalanceWithoutTaxes = useSelector(getCartOrderOutstandingBalanceWithoutTaxes);
  const stadiumPointsContribution = useSelector(getCartOrderStadiumPointsContribution);
  const stadiumPointsContributionWithoutTaxes = useSelector(
    getCartOrderStadiumPointsContributionWithoutTaxes,
  );
  const shopPointsContribution = useSelector(getCardOrderShopPointsContribution);
  const shopPointsContributionWithoutTaxes = useSelector(
    getCardOrderShopPointsContributionWithoutTaxes,
  );
  const walletMoneyContribution = useSelector(getCartOrderwalletMoneyContribution);
  const walletMoneyContributionWithoutTaxes = useSelector(
    getCartOrderWalletMoneyContributionWithoutTaxes,
  );
  const teamWalletContribution = useSelector(getCartOrderTeamPointsContribution);
  const teamWalletContributionWithoutTaxes = useSelector(
    getCartOrderTeamPointsContributionWithoutTaxes,
  );
  const orderCurrencyCode = useSelector(getCartOrderCurrency);

  const orderFetched = useSelector(getCartOrderFetched);

  const { isMobileDevice } = useContext(CurrentDeviceContext);
  const { convertPriceInDisplayStoreCurrency, formatPriceInDisplayStoreCurrency } =
    useStoreCurrencyConverter();
  const { previewMode } = usePreviewModeContext();

  const {
    cartTotal: orderCartTotal,
    cartItemsTotal,
    cartTotalExcludingTaxes,
    youPayAmount,
    setYouPayAmount,
    storeCurrencyConversionRate,
    authorizedPayments,
    taxTotal,
    pompData,
    platformFee: orderPlatformFee,
    taxRecoveryFee: orderTaxRecoveryFee,
    promoTotal,
    isAnyItemWithoutDigitalAsset,
    getCartTotalInSelectedCurrency,
  } = useCheckoutContext();

  const withAnalytics = useWithAnalytics();

  const {
    platformFee,
    taxRecoveryFee,
    cartTotal,
    stadiumPointsInUse,
    walletBalanceInUse,
    storeSpecificPointsInUse,
    teamWalletBalanceInUse,
  } = useMemo(() => {
    if (editable) {
      return {
        platformFee: 0,
        taxRecoveryFee: 0,
        cartTotal: cartTotalExcludingTaxes,
        stadiumPointsInUse: stadiumPointsContributionWithoutTaxes,
        walletBalanceInUse: walletMoneyContributionWithoutTaxes,
        storeSpecificPointsInUse: convertPriceInDisplayStoreCurrency(
          shopPointsContributionWithoutTaxes,
        ),
        teamWalletBalanceInUse: teamWalletContributionWithoutTaxes,
      };
    }
    return {
      platformFee: orderPlatformFee,
      taxRecoveryFee: orderTaxRecoveryFee,
      cartTotal: orderCartTotal,
      storeSpecificPointsInUse: convertPriceInDisplayStoreCurrency(shopPointsContribution),
      stadiumPointsInUse: stadiumPointsContribution,
      walletBalanceInUse: walletMoneyContribution,
      teamWalletBalanceInUse: teamWalletContribution,
    };
  }, [
    editable,
    orderPlatformFee,
    orderTaxRecoveryFee,
    orderCartTotal,
    cartTotalExcludingTaxes,
    stadiumPointsContribution,
    shopPointsContribution,
    walletMoneyContribution,
    stadiumPointsContributionWithoutTaxes,
    shopPointsContributionWithoutTaxes,
    walletMoneyContributionWithoutTaxes,
  ]);

  const {
    useWallet,
    useStadiumPoints: useStadiumPts,
    useStoreSpecificPoints,
    storeSpecificPointsBalance,
    stadiumPointsBalance: stadiumPoints,
    userWalletBalance,
    walletCurrency,
    currency,
    setCurrency,
    conversionRate,
    selectedTeamWallet,
    creditCardId,
    billingTaxNumber,
    setBillingTaxNumber,
    canPayNow,
    selectedAddress,
    billingType,
  } = useGlobalCheckoutContext();

  const storeBalance = convertPriceInDisplayStoreCurrency(storeSpecificPointsBalance);
  const isCorporateBilled = billingType === corporateBilled.key;

  const { code: currencyCode } = currency || {};

  const { id: selectedTeamWalletId, name: selectedTeamWalletName } = selectedTeamWallet || {};

  const walletConversionRate = conversionRates[walletCurrency?.code?.toLowerCase()];

  const { openUnavailableItemsModal, renderUnavailableItemsModal } = useUnavailableItemsModal();

  const {
    loading: isCheckoutLoading,
    onCheckout,
    updateOrder,
    setLoading: setIsCheckoutLoading,
  } = useOnCheckout({
    creditCardId,
    useWallet,
    openUnavailableItemsModal,
    isCorporateBilled,
    billingTaxNumber,
  });

  const onSuccessPromoCode = () => refreshCart(orderId);

  const stadiumWalletBalanceInUsd = userWalletBalance / walletConversionRate;

  const updateTaxNumber = (response) => {
    const taxNumber =
      response?.spreeLeadOrders?.[orderId]?.taxNumber ||
      response?.spreeOrganizerOrders?.[orderId]?.taxNumber;
    setBillingTaxNumber(taxNumber);
  };

  //update order country id
  //have not included selectedCountry in dependency array as we have defined
  //another useEffect on country change in navigation/right-section.js
  useEffect(() => {
    if (
      orderNumber &&
      orderCountryId !== selectedCountry.id &&
      orderNumber != PREVIEW_MODE_DEFAULT_ORDER_ID
    ) {
      const updatedCountryId = isGlobalCountry ? usCountryId : selectedCountry.id;
      updateOrder(
        {
          country_id: updatedCountryId,
          ...(giftDetailId
            ? { gift_detail_attributes: { id: giftDetailId, fe_for_eshop: true } }
            : {}),
        },
        updateTaxNumber,
      );
    }
  }, [orderNumber, giftDetailId]);

  const userPointsInStoreCurrency = useMemo(() => {
    let totalPts = 0;
    if (useStoreSpecificPoints) totalPts += Number.parseFloat(storeBalance);
    if (useStadiumPts)
      totalPts += convertPriceInDisplayStoreCurrency(stadiumPoints, POINTS_CURRENCY_CODE);
    return totalPts;
  }, [
    convertPriceInDisplayStoreCurrency,
    storeBalance,
    useStadiumPts,
    stadiumPoints,
    useStoreSpecificPoints,
  ]);

  const balance = cartTotal > userPointsInStoreCurrency ? cartTotal - userPointsInStoreCurrency : 0;

  const { currencyAmount } = useMemo(
    () =>
      getFormattedOrderCheckoutSummary(
        balance,
        conversionRate,
        storeCurrencyConversionRate,
        useWallet ? stadiumWalletBalanceInUsd : 0,
        selectedTeamWalletId,
        0,
      ),
    [
      useWallet,
      balance,
      conversionRate,
      storeCurrencyConversionRate,
      selectedTeamWalletId,
      stadiumWalletBalanceInUsd,
      displayCurrency,
    ],
  );

  useEffect(() => setYouPayAmount(currencyAmount), [currencyAmount]);

  useEffect(() => {
    if (showCurrencySelector && displayCurrency.code !== currency.code) {
      setCurrency(displayCurrency);
    }
  }, [displayCurrency, showCurrencySelector, currency]);

  const canShowGetAQuote = itemsCount > 0 && isLockerShop && editable;

  const chargeInSelectedCurrency = MIN_CARD_CHARGE_IN_USD * conversionRate;
  const showDisclaimer =
    currencyAmount > 0 && currencyAmount < chargeInSelectedCurrency && creditCardId;
  const charge = chargeInSelectedCurrency.toFixed(2);

  const currencyAmountToDisplay = useMemo(() => {
    const outstandingYouPayAmount = getCartTotalInSelectedCurrency({
      totalToConvert: editable ? outstandingBalanceWithoutTaxes : outstandingBalance,
    });

    return showCurrencySelector
      ? formatPriceInDisplayStoreCurrency(outstandingYouPayAmount, storeDisplayCurrencyCode, {
          formatZero: true,
        })
      : formatPrice(outstandingYouPayAmount, { currencyCode, currencyDisplay: "code" });
  }, [
    showCurrencySelector,
    storeDisplayCurrencyCode,
    currencyCode,
    currency,
    outstandingBalance,
    outstandingBalanceWithoutTaxes,
    editable,
    orderCurrencyCode,
    getCartTotalInSelectedCurrency,
  ]);

  const formatOrderSummaryRowValue = (
    value,
    { isDeduction = false, currency = storeDisplayCurrencyCode } = {},
  ) => {
    if (!value) return null;

    const formattedValue = formatPriceInDisplayStoreCurrency(value, currency);
    if (isDeduction) return `-${formattedValue}`;

    return formattedValue;
  };

  const formattedTeamWalletContribution = useMemo(
    () =>
      formatOrderSummaryRowValue(teamWalletBalanceInUse, {
        isDeduction: true,
        currency: orderCurrencyCode,
      }),
    [teamWalletBalanceInUse, orderCurrencyCode],
  );

  const handleCheckout = () => {
    setIsCheckoutLoading(true);
    withAnalytics(undefined, {
      track: true,
      target: PAY_TARGET,
      eventProperties: {
        shop_number: storeNumber,
        cart_value: orderCartTotal,
        item_count: itemsCount,
      },
    })();
    dispatch(refreshTotals(orderId)).then((order) => {
      if (!order.cartUpdated) {
        onCheckout({
          useStoreSpecificPoints,
          orderTotal: youPayAmount,
          currencyCodeId: currency?.id,
          selectedAddress,
          subTotal: cartItemsTotal,
        });
      } else {
        setIsCheckoutLoading(false);
        postCumulativeCartRefresh(order, { updateAttributesOnly: true });
      }
    });
  };

  const renderDisclaimer = () => {
    const chargeWithCurrency = `${currencyCode} ${charge}`;
    return showDisclaimer ? (
      <div className={styles.paymentChargeInfo}>
        Our payment system instantly charges {chargeWithCurrency} for orders under{" "}
        {chargeWithCurrency}. We&apos;ll credit your {MY_WALLET} the overage.
      </div>
    ) : null;
  };

  const setCheckoutAndDisplayCurrency = (currency) => {
    setCurrency(currency);
    setDisplayCurrency(currency);
  };

  const getOrderSummary = () => [
    {
      label: "Bag Total",
      value: formatOrderSummaryRowValue(cartItemsTotal),
      restrictTranslation: true,
    },
    {
      label: pompLabel,
      value: pompData?.showPomp ? pompData?.pompFeeToDisplay : "",
      restrictTranslation: true,
    },
    {
      label: "Total Tax",
      value: formatOrderSummaryRowValue(taxTotal),
      restrictTranslation: true,
    },
    {
      label: platformFeeLabel,
      value: orderPlatformFee > 0 && editable ? "TBD" : formatOrderSummaryRowValue(platformFee),
      tooltipText: platformFeeTooltipText,
      restrictTranslation: true,
    },
    {
      label: taxRecoveryFeeLabel,
      value: editable ? "TBD" : formatOrderSummaryRowValue(taxRecoveryFee),
      tooltipText: taxRecoveryFeeTooltipText,
      restrictTranslation: true,
    },
  ];

  const getMyStadiumPointsDisplayValue = () => {
    if (!stadiumPointsInUse) {
      return null;
    }

    const formattedPrice = formatPriceInDisplayStoreCurrency(stadiumPointsInUse, orderCurrencyCode);

    let pointsDisplay = `- ${formattedPrice}`;

    if (!isCustomCurrency(storeCurrencyCode)) {
      const convertedPoints = convertPrice({
        from: orderCurrencyCode,
        to: POINTS_CURRENCY_CODE,
        value: stadiumPointsInUse,
        conversionRates,
      });

      const formattedPoints = formatPrice(convertedPoints, {
        suffix: DEFAULT_STC_CURRENCY.name,
        currencyCode: POINTS_CURRENCY_CODE,
      });

      pointsDisplay += ` (${formattedPoints})`;
    }

    return pointsDisplay;
  };

  const walletInUseSummary = [
    {
      label: `shop ${storeCurrencyName}`,
      value: formatOrderSummaryRowValue(storeSpecificPointsInUse, {
        isDeduction: true,
      }),
      showWallet: useStoreSpecificPoints,
      restrictTranslation: true,
    },
    {
      label: "Stadium Points",
      value: getMyStadiumPointsDisplayValue(),
      restrictTranslation: true,
      showWallet: useStadiumPts,
    },
    {
      label: MY_WALLET.toUpperCase(),
      value: formatOrderSummaryRowValue(walletBalanceInUse, {
        isDeduction: true,
        currency: orderCurrencyCode,
      }),
      restrictTranslation: true,
      showWallet: useWallet,
    },
    {
      label: `Wallet: ${selectedTeamWalletName}`,
      value: formattedTeamWalletContribution,
      restrictTranslation: true,
      showWallet: selectedTeamWallet?.id,
    },
    {
      label: "Promo",
      value: formatOrderSummaryRowValue(!editable ? Math.abs(promoTotal) : 0, {
        isDeduction: true,
      }),
      restrictTranslation: true,
      showWallet: !!promoTotal,
    },
  ];

  const renderPlatformFeeNote = () => {
    if (!previewMode) return null;
    return (
      <div className={styles.shippingNote}>
        {platformFeeLabel}
        {taxRecoveryFeeLabel ? ` and ${taxRecoveryFeeLabel}` : ""} not added in demo mode.
      </div>
    );
  };
  const renderNote = () => {
    return (
      <div className={styles.shippingNoteLabel}>
        <i className={styles.shipIcon} /> Shipping {isLockerShop ? "& Basic Decoration" : ""}{" "}
        Included
      </div>
    );
  };

  const checkoutNotes = useMemo(() => {
    return [
      {
        key: "shippingIncluded",
        note: renderNote(),
      },
      {
        key: "platformFee",
        note: renderPlatformFeeNote(),
        showNote: previewMode,
      },
      {
        key: "cardDisclaimer",
        note: renderDisclaimer(),
        showNote: showDisclaimer,
      },
      {
        key: "assetMissingNote",
        note: ASSET_MISSING_NOTE,
        className: styles.assetMissingText,
        showNote: isLockerShop && isAnyItemWithoutDigitalAsset,
      },
    ];
  }, [isAnyItemWithoutDigitalAsset, isLockerShop, previewMode, showDisclaimer]);

  const summaryFooterNotes = [
    {
      key: "purchasingSwagNote",
      note: <SwagCartNote noteText={PURCHASING_SWAG_NOTE} className={styles.purchasingSwagNote} />,
      showNote: isLockerShop,
    },
  ];

  const renderViewQuoteBtn = () => {
    return (
      canShowGetAQuote && (
        <InternalButton
          styleType="primary-outline"
          disabled={!cartItemsTotal}
          className={styles.viewQuoteCta}
          onClick={() => {
            setShowGetAQuoteModal(true);
            postHubspotFormData({
              payload: [
                {
                  name: "email",
                  value: userEmail,
                },
              ],
              formGuid: HUBSPOT_LOCKER_SHOP_GET_A_CALL_FORM_GUID,
            });
          }}
        >
          View Quote
        </InternalButton>
      )
    );
  };

  const renderCurrencySelectorDropdown = () => {
    if (showCurrencySelector) {
      return (
        <CurrencySelector
          currencies={currencies}
          disabled={!isEmpty(authorizedPayments)}
          currency={displayCurrency}
          setCurrency={setCheckoutAndDisplayCurrency}
          className={styles.currencyDropdown}
        />
      );
    } else {
      return (
        <CheckoutCurrencySelector
          className={styles.currencyDropdown}
          initCurrencyId={orderCurrencyId}
          disabled={!isEmpty(authorizedPayments)}
        />
      );
    }
  };

  const renderCurrencySelector = () => (
    <div className={styles.payRow}>
      <div className={styles.summaryCol}>{renderCurrencySelectorDropdown()}</div>
      {isMobileDevice && <>{renderViewQuoteBtn()}</>}
    </div>
  );

  const renderPayNowCta = () => {
    if (editable) return null;

    if (previewMode) return <ExitDemoModeCta />;

    return (
      <div className={`${styles.fullBlockBtn} full-block-btn`}>
        <InternalButton
          styleType="primary"
          disabled={!canPayNow}
          isLoading={isCheckoutLoading}
          onClick={handleCheckout}
          className={styles.btnCheckout}
        >
          Pay Now
        </InternalButton>
      </div>
    );
  };

  const renderPayTodayDisplayValue = () => (
    <>
      {showDisclaimer && <span>{charge} </span>}
      {showDisclaimer ? (
        <span className={styles.cutPrice}>({currencyAmountToDisplay})</span>
      ) : (
        <span>{currencyAmountToDisplay}</span>
      )}
    </>
  );

  const renderPromoCodeSection = () =>
    !editable &&
    orderFetched && (
      <PromoCode
        key={promoCode}
        appliedPromoCode={promoCode}
        orderId={orderId}
        onSuccessPromoCode={onSuccessPromoCode}
        className={`${styles.noSpacing} ${styles.extraClassPromoBox}`}
      />
    );

  const renderCartCheckoutCta = () => {
    const checkoutButtonJuneSoProps = {
      componentName: YOUR_BAG_SELF,
      target: SEND_TO_MYSELF,
    };

    const nextButtonJuneSoProps = {
      componentName: YOUR_BAG_OTHER,
      target: NEXT,
    };

    return (
      <div className={`${styles.fullBlockBtn} full-block-btn`}>
        <CartCheckoutCta
          ctaJuneSoProps={isSendToMyself ? checkoutButtonJuneSoProps : nextButtonJuneSoProps}
          className={styles.btnCheckout}
        />
      </div>
    );
  };

  const renderCheckoutSummary = () => (
    <>
      {!isMobileDevice && renderCurrencySelector()}
      <article
        className={classNames(styles.orderSummarySection, cartCheckoutClass, {
          [styles.mobileFixedTopBar]: isMobileDevice,
          [styles.withAnnouncementBar]: appGenericAnnouncementBarFlagEnabled,
          [styles.orderSummarySectionV2]: isNewSwagmagicHost,
        })}
      >
        <OrderSummary
          isNewCheckoutFlow
          orderSummary={getOrderSummary()}
          walletInUseSummary={walletInUseSummary}
          payTodayText="You Pay"
          payTodayTotal={renderPayTodayDisplayValue()}
          currencySelector={renderCurrencySelectorDropdown()}
          promoCodeSelector={renderPromoCodeSection()}
          checkoutNotes={checkoutNotes}
          renderPayNowCta={editable ? renderCartCheckoutCta : renderPayNowCta}
          priceConversionLabel={renderViewQuoteBtn()}
          footerNotes={summaryFooterNotes}
        />
      </article>
      {isMobileDevice && renderPayNowCta()}
    </>
  );

  return (
    <section className={`${isPricelessShop ? styles.pricelessSummaryRightBlock : ""}`}>
      {!isPricelessShop && renderCheckoutSummary()}
      {children}
      {showGetAQuoteModal && (
        <GetAQuoteModal
          isMobileDevice={isMobileDevice}
          onCloseModal={() => {
            setShowGetAQuoteModal(false);
          }}
        />
      )}
      {renderUnavailableItemsModal()}
    </section>
  );
};

export default CartCheckout;
